import React, { forwardRef } from "react";
import { FormControl, MenuItem, Select, Checkbox, ListItemText } from "@mui/material";

const SelectInputComponent = forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      onKeyDown,
      options = [],
      error,
      required = false,
      label,
      multiple = false,
      renderValue: renderValueProp, // ✅ optional override (backwards compatible)
    },
    ref
  ) => {
    // Helper: find the display label for a given value
    const labelFor = (val) => {
      const opt = options.find((o) => o?.value === val);
      return opt?.label ?? String(val ?? "");
    };

    // Default renderValue that maps selected values to labels
    const defaultRenderValue = (selected) => {
      if (multiple) {
        const arr = Array.isArray(selected) ? selected : [];
        if (!arr.length) {
          // Placeholder look when empty
          return (
            <span style={{ color: "rgba(0, 0, 0, 0.6)" }}>
              {label} {required && <span>*</span>}
            </span>
          );
        }
        return arr.map(labelFor).join(", ");
      }

      // Single-select
      if (!selected) {
        return (
          <span style={{ color: "rgba(0, 0, 0, 0.6)" }}>
            {label} {required && <span>*</span>}
          </span>
        );
      }
      return labelFor(selected);
    };

    return (
      <FormControl
        fullWidth
        required={required}
        sx={{ marginBottom: 2 }}
        error={!!error}
        variant="outlined"
      >
        <Select
          multiple={multiple}
          value={value || (multiple ? [] : "")}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          inputRef={ref}
          displayEmpty
          // ✅ Use caller-supplied renderValue if provided; otherwise use our label-mapper
          renderValue={renderValueProp || defaultRenderValue}
        >
          {!multiple && (
            <MenuItem value="" disabled>
              {label}
            </MenuItem>
          )}

          {options.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
              {multiple ? (
                <>
                  <Checkbox
                    checked={
                      Array.isArray(value) &&
                      value.some((v) => v === option.value)
                    }
                  />
                  <ListItemText primary={option.label} />
                </>
              ) : (
                option.label
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
);

export default SelectInputComponent;